import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

import APIS from "@service/api";
import { getUser } from "@utils";

export const thirdPartyIntegrationApi = createApi({
	reducerPath: "thirdPartyIntegrationApi",
	refetchOnFocus: false,
	refetchOnReconnect: true,
	refetchOnMountOrArgChange: false,
	tagTypes: ["UserFavoriteGames"],
	baseQuery: fetchBaseQuery({
		baseUrl: APIS["third-party-api"],
		prepareHeaders: (headers) => {
			const user = getUser();

			if (user?.token) {
				headers.set("Authorization", `Bearer=${user.token}`);
			}

			return headers;
		},
	}),
	endpoints: (builder) => ({
		getCasinoContainerConfigs: builder.query({
			query: (params) => ({
				url: "casinounited/containers",
				method: "GET",
				params,
			}),
			providesTags: ["CasinoContainerConfigs"],
		}),
		getCasinoGames: builder.infiniteQuery({
			query: ({ pageParam, queryArg }) => ({
				url: "casinounited/games",
				method: "GET",
				params: pageParam ? { ...queryArg, offset: pageParam } : queryArg,
			}),
			providesTags: ["CasinoGames"],
			infiniteQueryOptions: {
				getNextPageParam: (lastPage, allPages) => {
					return lastPage.count * allPages.length;
				},
			},
		}),
		getCasinoGame: builder.mutation({
			query: (gameId) => ({
				url: "casinounited/games",
				method: "GET",
				params: { game_id: gameId, size: 1 },
			}),
		}),
		getCasinoGameByProvider: builder.query({
			query: ({ provider, game_id }) => ({
				url: `casinounited/games/${game_id}?provider=${provider}`,
				method: "GET",
			}),
		}),
		getLocalGameCategories: builder.query({
			query: (params) => ({
				url: "casinounited/categories",
				method: "GET",
				params,
			}),
			providesTags: ["LocalGameCategories"],
		}),
		getGameAffiliates: builder.query({
			query: (params) => ({
				url: "casinounited/games/distinct?field=affiliate",
				method: "GET",
				params,
			}),
			providesTags: ["GameAffiliates"],
		}),
		getUserFavoriteGames: builder.query({
			query: ({ size = 200, offset = 0 } = {}) => ({
				url: "casinounited/user-favorite-games",
				method: "GET",
				params: { size, offset },
			}),
			providesTags: ["UserFavoriteGames"],
		}),
		addUserFavoriteGame: builder.mutation({
			query: (gameId) => ({
				url: "casinounited/user-favorite-games",
				method: "POST",
				body: { gameId: String(gameId) },
			}),
			invalidatesTags: ["UserFavoriteGames"],
		}),
		removeUserFavoriteGame: builder.mutation({
			query: (gameId) => ({
				url: `casinounited/user-favorite-games/${encodeURIComponent(String(gameId))}`,
				method: "DELETE",
			}),
			invalidatesTags: ["UserFavoriteGames"],
		}),
	}),
});

export const {
	useGetCasinoContainerConfigsQuery,
	useGetLocalGameCategoriesQuery,
	useGetGameAffiliatesQuery,
	useGetCasinoGamesInfiniteQuery,
	useGetCasinoGameMutation,
	useGetUserFavoriteGamesQuery,
	useAddUserFavoriteGameMutation,
	useRemoveUserFavoriteGameMutation,
} = thirdPartyIntegrationApi;
